import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { useFetchLoginSuccess } from "../features/Authentication/useFetchLoginSuccess";
import { PermissionsProvider } from "./PermissionContext";
import { useGetSubClientAssignedPermissions } from "../components/ui/Sidebar/useGetSubClientAssignedPermissions";
import { useGetSubClientDetails } from "../hooks/useGetSubClientDetails";

interface AuthContextValue {
  user: any;
  updateUserInfo: (isProfileImageAvailable: boolean) => void;
  saveUserInLocal: (value: string) => void;
  logoutUser: () => void;
  isLoading: boolean;
  isSubClientAssignedPermissionLoading: boolean;
  userRole: string;
  clientName: string;
  isTransferStationBusinessTypeEnabled: boolean;
  isWaterMeterBusinessTypeEnabled: boolean;
  scaleDetailModel:
  | { scaleNumber: number; scaleHouseId: number }
  | null
  | undefined;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function AuthContextProvider({ children }: { children: ReactNode }) {
  // Lazy initialization to prevent parsing localStorage on every render
  const [localUser, setLocalUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") ?? "{}"),
  );

  const { isLoading: isFetchLoginSuccessLoading, data: loginSuccessData } =
    useFetchLoginSuccess({ isAuthenticated: !!localUser?.isAuthenticated });

  const currentSelectedClient = sessionStorage.getItem("currentSelectedClient");
  const selectedClientId = currentSelectedClient
    ? JSON.parse(currentSelectedClient)
    : null;

  const {
    isLoading: isSubClientAssignedPermissionLoading,
    data: subClientAssignedPermissions,
  } = useGetSubClientAssignedPermissions({
    roleType: selectedClientId?.role ?? loginSuccessData?.role ?? "",
    clientId: localUser?.clientId,
  });

  const { isFetching: isSubclientDetailsLoading, data: subClientDetails } =
    useGetSubClientDetails();

  // Memoize derived values to prevent unnecessary recalculations
  const userRole = useMemo(
    () => selectedClientId?.role ?? loginSuccessData?.role,
    [selectedClientId?.role, loginSuccessData?.role],
  )

  const subClientPermissions = useMemo(() => {
    const basePermissions = loginSuccessData?.subClientPermissions ?? [];
    return subClientAssignedPermissions
      ? basePermissions.concat(subClientAssignedPermissions)
      : basePermissions;
  }, [loginSuccessData?.subClientPermissions, subClientAssignedPermissions]);

  const updateUserInfo = (isProfileImageAvailable: boolean) => {
    const updatedLocalStorage = { ...localUser, isProfileImageAvailable };
    localStorage.setItem("user", JSON.stringify(updatedLocalStorage));
    setLocalUser(updatedLocalStorage);
  };

  const saveUserInLocal = (value: string) => {
    const parsedValue = JSON.parse(value);
    localStorage.setItem("user", value);
    setLocalUser(parsedValue);
  };

  const logoutUser = () => {
    localStorage.removeItem("user");
    sessionStorage.removeItem("currentSelectedClient");
    sessionStorage.clear();
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      user: localUser,
      updateUserInfo,
      saveUserInLocal,
      logoutUser,
      isLoading: isFetchLoginSuccessLoading || isSubclientDetailsLoading,
      isSubClientAssignedPermissionLoading,
      userRole,
      clientName:
        selectedClientId?.clientName || loginSuccessData?.clientName || "WDMS",
      isTransferStationBusinessTypeEnabled: !!subClientDetails?.isTransferStationBusinessTypeEnabled,
      isWaterMeterBusinessTypeEnabled: !!subClientDetails?.isWaterMeterBusinessTypeEnabled,
      scaleDetailModel: subClientDetails?.scaleDetailModel,
    }),
    [
      localUser,
      isFetchLoginSuccessLoading,
      isSubclientDetailsLoading,
      isSubClientAssignedPermissionLoading,
      userRole,
      selectedClientId?.clientName,
      loginSuccessData?.clientName,
      subClientDetails?.isTransferStationBusinessTypeEnabled,
      subClientDetails?.isWaterMeterBusinessTypeEnabled,
      subClientDetails?.scaleDetailModel,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <PermissionsProvider
        clientPermissions={loginSuccessData?.clientPermissions ?? []}
        subClientPermissions={subClientPermissions ?? []}
        permissions={loginSuccessData?.permissions ?? []}
      >
        {children}
      </PermissionsProvider>
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined)
    throw new Error("AuthContext was used outside of AuthContextProvider");
  return context;
}

export { AuthContextProvider, useAuth };
